import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { LocationState } from "@/data/model/Enums";
import type { ILocation } from "@/data/model/Location";
import { role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";

import { i18n, isInRole } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { LocationExportWindow } from "@/portal/modal/LocationExportWindow";
import { LocationSuspendModal } from "@/portal/modal/LocationSuspendModal";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class LocationTableController {
    private $element: JQuery;
    private dao: LocationDao;

    private dataTable: DataTable<ILocation>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<LocationDao>(LocationDao);
        const tableConfig: IDataTableConfig<ILocation> = {
            columnMapping: [
                {
                    name: i18n("general.fields.name"),
                    property: "name",
                    formatter: (m, p) => getLocationName(m)
                },
                {
                    name: i18n("general.fields.created"),
                    property: "created",
                    styles: { width: "15%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
            ],
            rowClass: (m) => (m.state === LocationState.Deleted ? "deleted" : undefined)!,
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: false,
            defaultSortProperty: "created",
        };

        if (isInRole(role.Admin)) {
            tableConfig.columnMapping.push({
                name: i18n("general.fields.state"),
                property: "state",
                styles: { width: "12%" },
                formatter: (m, p) => {
                    let styleClass = "kt-badge--primary";
                    switch (p) {
                        case LocationState.Active:
                            styleClass = "kt-badge--success";
                            break;
                        case LocationState.Deleted:
                            styleClass = "kt-badge--warning";
                            break;
                        case LocationState.Suspended:
                            styleClass = "kt-badge--danger";
                            break;
                    }

                    return (
                        '<div class="kt-badge ' +
                        styleClass +
                        ' kt-badge--inline">' +
                        i18n("enums.LocationState." + LocationState[p]) +
                        "</div>"
                    );
                },
            });

            tableConfig.columnMapping.push({
                name: "",
                property: "id",
                styles: { width: "52px", "text-align": "right" },
                formatter: (m) => {
                    if (m.state === LocationState.Deleted) return "";

                    return $(
                        '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-power-off"></i></a></span>'
                    ).click((e) => {
                        e.stopPropagation();
                        this.showSuspensionDialog(m);
                        return false;
                    });
                },
            });
        }

        tableConfig.columnMapping.push({
            name: "",
            property: "id",
            styles: { width: "67px", "text-align": "right" },
            formatter: (m) => {
                if (m.state === LocationState.Deleted) return "";

                return $(
                    '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                );
            },
        });

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        if (isInRole(role.Admin))
            this.search.addDropdownFilterFromEnum(
                "state",
                LocationState,
                "enums.LocationState",
                i18n("location.allStates")
            );

        this.dataTable = new DataTable<ILocation>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                if (m.state !== LocationState.Active) return;

                location.href = "/location/" + m.id;
            },
            (m) => {
                if (m.state !== LocationState.Active) return;

                window.open("/location/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);

        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.export").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            new LocationExportWindow().show();
        });
    }

    private showSuspensionDialog(location: ILocation) {
        const m = new LocationSuspendModal(location);
        m.onStateChanged = () => this.dataTable.loadData();
        m.show();
    }
}
